import { useCallback, useEffect, useMemo } from 'react'

import { Nullable } from '@types'

import { ChipItemData } from '@components/ChipList'
import alertsApi from '@modules/alerts/api'
import { useAuthContext } from '@modules/auth/hooks/useAuthContext'
import savedSearchApi from '@modules/savedSearch/api'
import { SavedSearch } from '@modules/savedSearch/types'
import searchApi from '@modules/search/api'
import { FiltersSetup, ProfileType, SelectedFiltersMapByProfiles } from '@modules/search/types'
import { useContactHistory } from '@shared/hooks/useContactHistory'
import { useFeatureFlags } from '@shared/hooks/useFeatureFlags'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { FiltersSection, ResultsSection, SearchSection } from './components'
import { DEFAULT_PAGE_LIMIT, HISTORY_FILTER_OPTIONS } from './constants'
import { useSearchProfiles } from './hooks/useSearchProfiles'
import { useSearchState } from './hooks/useSearchState'
import { ContentSectionContainer, FilterSectionContainer, SearchInputSectionContainer, SearchPageInner } from './index.styles'

const Search = () => {
  const {
    selectedSortId,
    selectedGroupingId,
    metaFilters,
    page,
    scrollX,
    scrollY,
    searchString,
    selectedMetaFilter,
    expandedFilter,
    filtersSetup,
    selectedFiltersByProfiles,
    isSearchAllProfilesChecked,
    searchableProfilesMap,
    filterSectionExpandAllOptions,
    filterSectionOptionsSearch,
    setAppliedSavedSearchName,
    appliedSavedSearchName,
    savedSearchId,
    searchHistoryTypeId,
    setSavedSearchId,
    handleClearSection,
    handleSubmitMetaFilter,
    setFiltersSetup,
    resetAll,
    setPage,
    setScrollX,
    setScrollY,
    handleSearch,
    handleSort,
    handleGroupSelected,
    toggleItemFilter,
    handleExpandFilter,
    handleSetSearchableProfiles,
    handleSetAllSearchableProfiles,
    setFilterSectionExpandAllOptions,
    setFilterSectionOptionsSearch,
    setSearchHistoryTypeId,
    eventsFilterData,
    setEventsFilter,
  } = useSearchState()
  const queryClient = useQueryClient()

  const { user } = useAuthContext()
  const { hasAccessToSavedSearch } = useFeatureFlags()
  const isFilterApplied = useMemo(
    () =>
      !!Object.values(selectedFiltersByProfiles as SelectedFiltersMapByProfiles).flatMap(values => Object.values(values)?.flat())
        ?.length,
    [selectedFiltersByProfiles],
  )

  const {
    fetchCompanyIds = false,
    hasAttended,
    onlyMine = undefined,
    exclude = false,
  } = useMemo(() => {
    const historyFilterOption = HISTORY_FILTER_OPTIONS.find(option => option.id === searchHistoryTypeId)?.data

    return historyFilterOption
      ? { fetchCompanyIds: true, ...historyFilterOption }
      : { fetchCompanyIds: false, onlyMine: undefined, exclude: undefined, hasAttended: undefined }
  }, [searchHistoryTypeId])

  const companyIds = useContactHistory(fetchCompanyIds, { onlyMine, hasAttended, exclude })
  const { data: savedSearches } = useQuery({
    queryKey: [savedSearchApi.getSavedSearches.key],
    queryFn: () => savedSearchApi.getSavedSearches.fn(),
    enabled: hasAccessToSavedSearch,
  })
  const userEventsOptions = useMemo(() => {
    const events = [...(user?.events ?? [])]?.sort(
      (eventPrev, eventNext) => new Date(eventNext.startDate).getTime() - new Date(eventPrev.startDate).getTime(),
    )

    return events.map(event => ({
      label: event.name,
      value: event.id,
      hidden: event.hidden,
    }))
  }, [user?.events])

  const mergeSearchableFilters = useCallback(
    (incomingFilters: FiltersSetup) => {
      setFiltersSetup((currentFiltersSetup: FiltersSetup) => {
        /** disabling all filters, so only those which came will be enabled */
        const initialState = Object.entries(currentFiltersSetup || {}).reduce<FiltersSetup>(
          (acc, [filter, data]) => ({ ...acc, [filter]: { ...data, total: 0, enabled: !isFilterApplied } }),
          {},
        )
        return Object.entries(incomingFilters || {}).reduce((acc, [filter, data]) => {
          if (acc[filter]) {
            acc[filter].total = data.total || 0
            acc[filter].facets = data.facets
            acc[filter].enabled = true
          } else {
            acc[filter] = {
              ...data,
              enabled: true,
            }
          }

          return acc
        }, initialState)
      })
    },
    [isFilterApplied],
  )

  const searchableEntities = useMemo(
    () =>
      Object.keys(searchableProfilesMap).filter(
        type => isSearchAllProfilesChecked || !!searchableProfilesMap[type as ProfileType],
      ) as ProfileType[],
    [searchableProfilesMap, isSearchAllProfilesChecked],
  )

  const isGroupByDisabled = useMemo(
    () => !(isSearchAllProfilesChecked || searchableProfilesMap[ProfileType.company]),
    [isSearchAllProfilesChecked, searchableProfilesMap],
  )

  const isSortingDisabled = useMemo(() => Boolean(selectedGroupingId || searchString), [searchString, selectedGroupingId])

  const { data: { eventsYears } = {} } = useQuery({
    queryKey: [searchApi.getMetaFiltersData.key],
    queryFn: () => searchApi.getMetaFiltersData.fn(),
  })

  const from = DEFAULT_PAGE_LIMIT * (page - 1)

  const { data: { result: searchResult = [], total = 0, isResultGrouped = false } = {}, rawSearchParams } = useSearchProfiles({
    searchString,
    selectedFiltersByProfiles,
    from,
    searchableEntities,
    isSearchAllProfilesChecked,
    searchableProfilesMap,
    savedSearchId,
    selectedSortId,
    /** @ts-expect-error will be fixed soon */
    mergeSearchableFilters,
    metaFilters,
    selectedGroupingId,
    eventsFilterData,
    onSearchSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [alertsApi.fetchProfilesAlerts.key] })
    },
    companyHistoryFilter: {
      companyIds: fetchCompanyIds ? companyIds : undefined,
      exclude,
    },
  })

  useEffect(() => {
    if (searchResult?.length && (scrollX || scrollY)) {
      window.scrollTo({
        top: scrollY,
        left: scrollX,
        behavior: 'smooth',
      })

      // We need to use setTimeout in this case due to Safari-only issue.
      // `window.scrollTo` works great in all other browsers, but not in Safari.
      // So that is why we are wrapping `window.scroll` into setTimeout here.
      setTimeout(() => {
        window.scroll({ top: scrollY, left: scrollX, behavior: 'smooth' })
      }, 10)
    }
  }, [searchResult?.length, scrollX, scrollY])

  const { data: searchResponse } = useQuery({
    queryKey: [searchApi.getFilters.key],
    queryFn: () =>
      searchApi.getFilters.fn({
        from: 0,
        size: 10,
        types: Object.keys(searchableProfilesMap).map(type => ({ type, isSelected: true })),
      }),
    enabled: !filtersSetup,
  })

  useEffect(() => {
    if (searchResponse) {
      const { facets } = searchResponse
      // @ts-expect-error type mismatch
      mergeSearchableFilters(facets)
    }
  }, [searchResponse])

  const totalPages = useMemo(() => (total ? Math.ceil(total / DEFAULT_PAGE_LIMIT) : 0), [total])

  const onDeleteChip = useCallback(
    (chipItem: ChipItemData) => {
      const metadata = chipItem.metaData as { filterType: ProfileType; field: string }

      toggleItemFilter(metadata.filterType, metadata.field || '', chipItem.key, false)
    },
    [toggleItemFilter],
  )

  const setSavedSearch = (savedSearch: Nullable<SavedSearch>) => {
    setSavedSearchId(savedSearch?.id ?? null)
    setAppliedSavedSearchName(savedSearch?.name ?? '')
  }

  const { mutate: markViewedProfiles } = useMutation({
    mutationFn: savedSearchApi.markViewedProfiles.fn,
    onSuccess: (_: unknown, data) => {
      queryClient.setQueryData<(string | number)[]>([savedSearchApi.runSavedSearch.key, savedSearchId], (oldData = []) => {
        return [...oldData, ...data.profileIds]
      })
    },
  })
  const onProfileNavigate = useCallback(
    (id: string | number) => {
      if (savedSearchId) {
        markViewedProfiles({ id: savedSearchId, profileIds: [id] })
      }
    },
    [savedSearchId, markViewedProfiles],
  )

  return (
    <SearchPageInner>
      <FilterSectionContainer>
        <FiltersSection
          filtersSetup={filtersSetup}
          searchableEntitiesMap={searchableProfilesMap}
          isAllProfilesEnabled={isSearchAllProfilesChecked}
          toggleItemFilter={toggleItemFilter}
          selectedFiltersByProfiles={selectedFiltersByProfiles}
          handleClearSection={handleClearSection}
          expandedFilter={expandedFilter}
          handleExpandFilter={handleExpandFilter}
          filterSectionExpandAllOptions={filterSectionExpandAllOptions}
          filterSectionOptionsSearch={filterSectionOptionsSearch}
          setFilterSectionExpandAllOptions={setFilterSectionExpandAllOptions}
          setFilterSectionOptionsSearch={setFilterSectionOptionsSearch}
        />
      </FilterSectionContainer>
      <ContentSectionContainer>
        <SearchInputSectionContainer>
          <SearchSection
            setSavedSearch={setSavedSearch}
            appliedSavedSearchName={appliedSavedSearchName}
            searchString={searchString}
            handleSearchClick={handleSearch}
            savedSearches={savedSearches}
            handleResetAll={resetAll}
            selectedFiltersByProfiles={selectedFiltersByProfiles}
            handleSetSearchableProfiles={handleSetSearchableProfiles}
            searchableProfilesMap={searchableProfilesMap}
            handleSetAllSearchableProfiles={handleSetAllSearchableProfiles}
            isAllProfilesEnabled={isSearchAllProfilesChecked}
          />
        </SearchInputSectionContainer>

        <ResultsSection
          savedSearchId={savedSearchId ?? undefined}
          onProfileNavigate={onProfileNavigate}
          appliedSavedSearchName={appliedSavedSearchName}
          isFilterApplied={isFilterApplied}
          isGroupByDisabled={isGroupByDisabled}
          selectedFiltersByProfiles={selectedFiltersByProfiles}
          searchString={searchString}
          onDeleteChip={onDeleteChip}
          total={total}
          resultProfiles={searchResult}
          totalPages={totalPages}
          page={page}
          selectedSortId={selectedSortId}
          sortDisabled={isSortingDisabled}
          onSort={handleSort}
          onGroup={handleGroupSelected}
          selectedGroupingId={selectedGroupingId}
          setPage={setPage}
          setScrollX={setScrollX}
          setScrollY={setScrollY}
          selectedMetaFilter={selectedMetaFilter}
          eventsYears={eventsYears}
          isResultGrouped={isResultGrouped}
          onSubmitMetaFilter={handleSubmitMetaFilter}
          userEventsOptions={userEventsOptions}
          selectedHistoryTypeId={searchHistoryTypeId || 0}
          handleHistoryTypeSelected={setSearchHistoryTypeId}
          handleSubmitEventsFilter={setEventsFilter}
          eventsFilterData={eventsFilterData}
          searchBody={rawSearchParams}
        />
      </ContentSectionContainer>
    </SearchPageInner>
  )
}

export default Search
